import {
  eventAnchorId,
  eventCoversDate,
  eventTitle,
  formatIsoDate,
} from "../../lib/explorer";
import type { BlockingEvent } from "../../types/explorer";
import {
  CNN_ONLY_TONE,
  EVENT_STAGE_TEXT_TONE,
  EVENT_STAGE_TONE,
  PATTERN_TONE,
} from "./eventBadgeStyles";
import { ScopeBadge } from "./ScopeBadge";
import { GlossaryLink } from "../GlossaryLink";
import {
  patternGlossaryId,
  stageGlossaryId,
} from "../../content/siteCatalog";

interface TimelinePreviewProps {
  events: BlockingEvent[];
  selectedDate: string;
  onSelectDate: (date: string) => void;
}

const STAGE_LEGEND = [
  {
    id: "onset",
    gloss: "forming",
    meaning: "The block is forming — early in the event window.",
  },
  {
    id: "mature",
    gloss: "established",
    meaning: "The block is established and fully developed.",
  },
  {
    id: "decay",
    gloss: "breaking down",
    meaning: "The block is breaking down — late in the event window.",
  },
  {
    id: "unknown",
    gloss: "unclear",
    meaning: "Too short, or no clear stage in this window.",
  },
] as const;

const CNN_ONLY_MEANING = "Flagged by the CNN, missed by GTD / TM90 / DG83 / S04.";

const STAGE_MEANING: Record<string, string> = Object.fromEntries(
  STAGE_LEGEND.map((item) => [item.id, item.meaning]),
);

export const TimelinePreview = ({
  events,
  selectedDate,
  onSelectDate,
}: TimelinePreviewProps) => {
  return (
    <section id="event-windows" className="mt-10 mb-8 scroll-mt-24">
      <h2 className="mb-2 font-display text-2xl font-semibold text-ink">
        Event windows
      </h2>
      <p className="mb-3 text-sm text-muted">
        Who labelled each spell, its stage, and peak{" "}
        <GlossaryLink termId="p-blocked">CNN P(blocked)</GlossaryLink>. Click a
        row to jump the map. Stage and pattern labels open their{" "}
        <GlossaryLink termId="blocking">Science definitions</GlossaryLink>.
      </p>

      <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted">
        {STAGE_LEGEND.map((item) => (
          <span
            key={item.id}
            className="inline-flex items-center gap-1.5"
            title={item.meaning}
          >
            <GlossaryLink
              termId={stageGlossaryId(item.id)}
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${EVENT_STAGE_TONE[item.id]}`}
            >
              {item.id}
            </GlossaryLink>
            <span className={EVENT_STAGE_TEXT_TONE[item.id]}>{item.gloss}</span>
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5" title={CNN_ONLY_MEANING}>
          <GlossaryLink termId="cnn-only" className={CNN_ONLY_TONE}>
            CNN-only
          </GlossaryLink>
          <span className="text-warn">missed by established indices</span>
        </span>
      </div>

      <ul className="space-y-3">
        {events.map((event) => {
          const active = eventCoversDate(event, selectedDate);
          const patternTermId = event.patternLabel
            ? patternGlossaryId(event.patternLabel)
            : null;
          return (
            <li
              key={event.id}
              id={eventAnchorId(event)}
              className="scroll-mt-24"
            >
              <button
                type="button"
                onClick={() => onSelectDate(event.startDate)}
                aria-current={active ? "true" : undefined}
                className={[
                  "panel w-full px-4 py-4 text-left transition",
                  active
                    ? "border-accent bg-accent/10 ring-2 ring-accent"
                    : event.cnnOnly
                      ? "border-warn/50 bg-warn/[0.07] hover:border-accent/40"
                      : "hover:border-accent/40",
                ].join(" ")}
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink">
                        {eventTitle(event)}
                      </span>
                      <span className="text-sm text-muted">
                        {formatIsoDate(event.startDate)}
                        {event.endDate !== event.startDate
                          ? ` – ${formatIsoDate(event.endDate)}`
                          : ""}
                      </span>
                      <span className="text-accent">{event.region}</span>
                      {event.patternLabel ? (
                        <span
                          className={PATTERN_TONE}
                          title={
                            patternTermId
                              ? "Circulation pattern — see Science Labels & terminology"
                              : "Circulation pattern"
                          }
                        >
                          Pattern · {event.patternLabel}
                        </span>
                      ) : null}
                      {event.cnnOnly ? (
                        <span className={CNN_ONLY_TONE} title={CNN_ONLY_MEANING}>
                          CNN-only
                        </span>
                      ) : null}
                      {event.stage ? (
                        <span
                          title={STAGE_MEANING[event.stage]}
                          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${EVENT_STAGE_TONE[event.stage]}`}
                        >
                          {event.stage}
                        </span>
                      ) : null}
                    </div>
                    {event.notes ? (
                      <p className="mt-2 text-sm text-muted">
                        {event.notes}
                      </p>
                    ) : null}
                    <p className="mt-2 text-xs text-faint">
                      Sources:{" "}
                      {event.sources.length
                        ? event.sources.join(" · ")
                        : "CNN (unlabelled)"}
                    </p>
                    {event.scope ? (
                      <div className="mt-3">
                        <ScopeBadge scope={event.scope} linkToGlossary={false} />
                      </div>
                    ) : null}
                  </div>

                  <div className="shrink-0 sm:text-right">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">
                      Peak CNN P(blocked)
                    </p>
                    <p className="mt-1 font-display text-4xl font-semibold leading-none text-ink">
                      {(event.probability * 100).toFixed(0)}%
                    </p>
                    <p className="mt-2 text-xs text-faint">Highest day in this window</p>
                  </div>
                </div>
              </button>
              {event.references?.length ? (
                <div className="ml-4 mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
                  <span className="font-semibold uppercase tracking-[0.12em] text-faint">
                    Evidence
                  </span>
                  {event.references.map((reference) => (
                    <a
                      key={reference.url}
                      href={reference.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-info hover:underline"
                    >
                      {reference.label} ↗
                    </a>
                  ))}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
};
